import { useRef, useState } from "react";
import { api, formatError } from "../../lib/api";
import { BrandLogo, refreshSiteConfig, useSiteConfig } from "../../lib/siteConfig";
import { Button } from "../../components/ui/button";
import { Upload, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { ChangePasswordCard } from "../../components/account/ChangePasswordCard";

function LogoTile({ variant, cfg, onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const hasLogo = variant === "light" ? !!cfg?.logo_url : !!cfg?.logo_url_dark;

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      await api.post(`/site/logo?variant=${variant}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshSiteConfig();
      toast.success(`${variant === "light" ? "Light" : "Dark"} logo updated`);
      onDone?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const bg = variant === "light" ? "bg-background" : "bg-primary";
  return (
    <div className="border border-border rounded-lg p-5" data-testid={`logo-tile-${variant}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">{variant === "light" ? "Light variant" : "Dark variant"}</div>
      <div className={`p-6 rounded-md ${bg} border border-border flex items-center justify-center`}>
        <BrandLogo size={72} rounded="rounded-lg" variant={variant} />
      </div>
      <div className="text-xs text-muted-foreground mt-3">
        {hasLogo ? "Custom logo uploaded." : "Using default sprout icon."}
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid={`logo-file-input-${variant}`} />
      <Button variant="outline" className="w-full mt-4" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`logo-upload-btn-${variant}`}>
        <Upload size={14} className="mr-2" />{busy ? "Uploading…" : (hasLogo ? "Replace" : "Upload")}
      </Button>
    </div>
  );
}

export default function SettingsPage() {
  const cfg = useSiteConfig();

  return (
    <div className="p-6 md:p-10 max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <SettingsIcon className="text-primary" />
        <h1 className="heading text-3xl md:text-4xl">Site settings</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-8">Brand identity shown in the header, sidebar and email templates.</p>

      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Logo</div>
      <p className="text-xs text-muted-foreground mb-4">Upload a light logo for bright surfaces and a dark logo for dark ones. PNG or JPG, max 10 MB. Square works best.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <LogoTile variant="light" cfg={cfg} />
        <LogoTile variant="dark" cfg={cfg} />
      </div>

      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mt-10 mb-3">Account security</div>
      <ChangePasswordCard />
    </div>
  );
}
